import { useEffect, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import CardView from '../components/Card/CardView'
import ThemeToggle from '../components/Common/ThemeToggle'
import Icon from '../components/Common/Icon'
import DanmakuLayer from '../components/Danmaku/DanmakuLayer'
import { useGameStore } from '../store/gameStore'
import { loadCards } from '../data/cardLoader'
import { getSection } from '../data/chapters'

function StudyPage() {
  const { chapterId, sectionId } = useParams()
  const [searchParams] = useSearchParams()
  const startIndex = parseInt(searchParams.get('card') ?? '0')
  const { currentCard, loadSection, nextCard, submitAnswer, getProgress, isInitialized, initialize, recordStudyLocation } = useGameStore()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const chapter = parseInt(chapterId ?? '1')
  const section = sectionId ?? 's1'

  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [isInitialized, initialize])

  useEffect(() => {
    const loadSectionCards = async () => {
      try {
        setLoading(true)
        setError(null)
        const cards = await loadCards(chapter, section)
        loadSection(cards, false)
        recordStudyLocation(chapter, section)
        // 跳到指定卡片
        if (startIndex > 0) {
          for (let i = 0; i < startIndex; i++) {
            useGameStore.getState().nextCard()
          }
        }
        setLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载失败')
        setLoading(false)
      }
    }

    if (isInitialized) {
      loadSectionCards()
    }
  }, [chapter, section, isInitialized, loadSection, startIndex])

  const sectionInfo = getSection(chapter, section)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center crt-scanlines">
        <div className="text-center">
          <div className="font-pixel text-lg text-[color:var(--color-bull)] pixel-blink mb-3">
            ▶ 加载中
          </div>
          <div className="font-pixel-display text-xs text-[color:var(--color-text-dim)]">
            加载卡片中...
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 crt-scanlines">
        <div className="pixel-box p-6 max-w-md text-center border-[color:var(--color-bear)]">
          <div className="font-pixel text-base text-[color:var(--color-bear)] mb-3 pixel-blink inline-flex items-center gap-2">
            <Icon name="cross" size={16} />
            加载出错
          </div>
          <p className="font-pixel-body text-base text-[color:var(--color-text)] mb-4">{error}</p>
          <Link to="/" className="pixel-button">
            ◀ 返回
          </Link>
        </div>
      </div>
    )
  }

  if (!currentCard) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 crt-scanlines">
        <div className="pixel-box pixel-box-bright p-8 max-w-md text-center">
          <div className="font-pixel text-2xl text-[color:var(--color-gold)] mb-4 pixel-blink inline-flex items-center justify-center gap-3">
            <Icon name="star" size={18} />
            通关
            <Icon name="star" size={18} />
          </div>
          <p className="font-pixel-body text-xl text-[color:var(--color-text-bright)] mb-6">
            恭喜！本小节已完成
          </p>
          <Link to="/" className="pixel-button pixel-button-primary">
            ◀ 返回首页
          </Link>
        </div>
      </div>
    )
  }

  const progress = getProgress()

  return (
    <div className="min-h-screen p-3 md:p-5">
      {/* 答题弹幕浮层 —— 监听 store.pendingDanmaku, streak 达标时飘 Z 哥口吻反馈 */}
      <DanmakuLayer />
      <div className="max-w-3xl mx-auto">
        {/* ========== 顶部返回区 ========== */}
        <div className="mb-3 flex items-center justify-between">
          <Link
            to="/"
            className="font-pixel-display text-[10px] text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] transition-colors"
          >
            ◀ 返回章节地图
          </Link>
          <ThemeToggle />
        </div>

        {/* ========== 章节信息 ========== */}
        <div className="pixel-box p-3 mb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)] mb-1">
                第 {String(chapter).padStart(2, '0')} 章 · 第 {section.slice(1)} 节
              </div>
              <h1 className="font-pixel-body text-lg font-bold text-[color:var(--color-text-bright)]">
                {sectionInfo?.title ?? section}
              </h1>
            </div>
            {sectionInfo && (
              <div className="pixel-tag text-[color:var(--color-cyan)] border-[color:var(--color-cyan)]">
                {sectionInfo.cardIds.length} 张卡片
              </div>
            )}
          </div>
          {sectionInfo && (
            <div className="mt-2 pt-2 border-t-2 border-[color:var(--color-border)]">
              <div className="font-pixel text-[9px] text-[color:var(--color-text-dim)] mb-1 tracking-wider">
                ► 学习目标
              </div>
              <ul className="space-y-0.5 font-pixel-body text-sm text-[color:var(--color-text)]">
                {sectionInfo.learningObjectives.map((obj, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[color:var(--color-gold)]">▸</span>
                    <span>{obj}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ========== 卡片组件 ========== */}
        <CardView
          card={currentCard}
          onNext={nextCard}
          onChoice={submitAnswer}
          progress={progress}
        />
      </div>
    </div>
  )
}

export default StudyPage